/*
 * File: [page].tsx
 * Project: meki
 * File Created: Friday, 11th June 2021 8:03:17 pm
 * Author: Gabriel Ulloa (gabriel@inventures.cl)
 * -----
 * Last Modified: Monday, 27th November 2023 5:50:39 pm
 * Modified By: Blanca Munizaga (blanca@inventures.cl)
 * -----
 * Copyright 2019 - 2021 Incrementa Ventures SpA. ALL RIGHTS RESERVED
 * Terms and conditions defined in license.txt
 * -----
 * Inventures - www.inventures.cl
 */
import { isApolloError } from '@apollo/client';
import { initializeApollo } from '@config/apollo';
import {
  getCategoryProducts,
  getCategoryProductsQueryParams,
  getCategoryProductsQueryResponse,
} from '@queries/category';
import {
  getHomeProducts,
  getHomeProductsQueryParams,
  getHomeProductsQueryResponse,
} from '@queries/product/queries/homeProducts';
import { Console } from '@utils';
import { GetStaticProps } from 'next';

import { useRouter } from 'next/router';
import { SEOHead } from '@components/seo';
import { CategoryIndex } from '@screens/category';
import { NO_API_STATIC_GENERATION } from '@config/environment';

interface CategorysProps {
  data: getCategoryProductsQueryResponse;
}
export default function Categorys({ data }: CategorysProps) {
  const router = useRouter();
  return (
    <>
      <SEOHead
        title={`Meki · ${
          /reciente/.exec(router.query.categorySlug as string)
            ? 'Productos Recientes'
            : /destacados/.exec(router.query.categorySlug as string)
            ? 'Destacados'
            : data?.bestSellingProductsCategory.nodes[0]?.categories.find(
                (category) => category.slug == router.query.categorySlug,
              )?.name ?? ''
        } · ${router.query.page as string}`}
      />
      <CategoryIndex initialData={data} />
    </>
  );
}
export async function getStaticPaths() {
  if (NO_API_STATIC_GENERATION) return { fallback: true, paths: [] };
  const apolloClient = initializeApollo();
  const {
    data: { homeCategories },
  } = await apolloClient.query<
    getHomeProductsQueryResponse,
    getHomeProductsQueryParams
  >({
    query: getHomeProducts(),
  });
  const paths = await Promise.all(
    homeCategories.map((category) => {
      return `/categoria/${category.slug}/1`;
    }),
  );
  return {
    paths: paths.flat(),
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps = async (data) => {
  const {
    params: { categorySlug, page },
  } = data;
  const apolloClient = initializeApollo();

  if (!+page || !Number.isInteger(+page)) {
    return {
      redirect: {
        permanent: true,
        destination: `/categoria/${categorySlug as string}/1`,
      },
    };
  }
  try {
    const { data } = await apolloClient.query<
      getCategoryProductsQueryResponse,
      getCategoryProductsQueryParams
    >({
      query: getCategoryProducts(),
      variables: {
        params: {
          pageNumber: +page > 1 ? +page : 1,
          slug: categorySlug as string,
          sortBy: {
            price: false,
            alphabetically: false,
            mostSelling: true,
          },
        },
        categoryParams: {
          slug: categorySlug as string,
        },
      },
    });
    if (!data) {
      return {
        props: {
          data: null,
          statusCode: 404,
        },
        revalidate: 1,
      };
    }
    return {
      props: {
        data,
        statusCode: 200,
      },
      revalidate: 60 * 60 * 4, // 4 hours
    };
  } catch (e) {
    Console.error(
      '[CATEGORY] Error prefetching category ' +
        (typeof categorySlug === 'string'
          ? categorySlug
          : categorySlug.join(' · ')),
    );
    Console.error(e);
    if (isApolloError(e)) {
      for (const error of e.graphQLErrors) {
        Console.log(error);
      }
      Console.log(e.networkError);
    }
    return {
      props: {},
      revalidate: 1, // 1 second
    };
  }
};
